import type {InfiniteData} from '@tanstack/react-query';
import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
} from '@tanstack/react-query';
import type {
  IndicatorsByMemberListKindItem,
  IndicatorsByMemberListParams,
} from 'api/v2';
import {
  IndicatorsByMemberListScope,
  indicatorsByMemberList,
  indicatorsFeedbackCreate,
  indicatorsFeedbackFormList,
  indicatorsKindsList,
  programsList,
} from 'api/v2';
import type {
  ActiveFeedbackForm,
  FeedbackForm,
  FormDefinition,
  IndicatorsByMemberPage,
  MyFeedback,
  ProgramSummary,
  SignalKind,
  SubmittedAnswerRequest,
} from './types';
import {normalizeFormDefinition} from './types';

type ByMemberParams = {
  program: string | undefined;
  kinds: string[] | undefined;
  scope: IndicatorsByMemberListParams['scope'];
  ordering: IndicatorsByMemberListParams['ordering'];
  member: string | undefined;
  pageSize: number | undefined;
};

type ByMemberFilters = {
  program: string;
  kinds: string[];
  scope?: IndicatorsByMemberListParams['scope'];
  ordering?: IndicatorsByMemberListParams['ordering'];
  member?: string;
  pageSize?: number;
};

export function filtersToParams(filters: ByMemberFilters): ByMemberParams {
  return {
    program: filters.program === 'all' ? undefined : filters.program,
    kinds: filters.kinds.length > 0 ? filters.kinds : undefined,
    scope: filters.scope,
    ordering: filters.ordering,
    member: filters.member,
    pageSize: filters.pageSize,
  };
}

// The cursor lives as a query param inside the absolute next/previous URLs.
export function extractCursor(
  next: string | null | undefined,
): string | undefined {
  if (!next) return undefined;
  return new URL(next).searchParams.get('cursor') ?? undefined;
}

// common's paramsSerializer stringifies undefined into "undefined", so unset
// params must be omitted entirely or the API 400s
function definedParams(
  params: IndicatorsByMemberListParams,
): IndicatorsByMemberListParams {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined),
  );
}

async function fetchIndicatorsByMember(
  params: ByMemberParams,
  cursor?: string,
): Promise<IndicatorsByMemberPage> {
  const {data} = await indicatorsByMemberList(
    definedParams({
      program: params.program,
      // Kind slugs come from the kinds endpoint; the generated enum lags new producers
      kind: params.kinds as IndicatorsByMemberListKindItem[] | undefined,
      scope: params.scope,
      ordering: params.ordering,
      member: params.member,
      cursor,
      pageSize: params.pageSize,
    }),
  );
  return data;
}

function errorStatus(error: unknown): number | undefined {
  const maybe = error as {status?: number; response?: {status?: number}};
  return maybe?.status ?? maybe?.response?.status;
}

// The endpoint returns the one active form or 404; the generated type says list.
async function fetchFeedbackForm(): Promise<FeedbackForm | null> {
  try {
    const response = await indicatorsFeedbackFormList();
    const form = response.data as unknown as ActiveFeedbackForm;
    return {
      ...form,
      definition: normalizeFormDefinition(form.definition as FormDefinition),
    };
  } catch (error) {
    if (errorStatus(error) === 404) return null;
    throw error;
  }
}

export type SubmitFeedbackResult =
  {status: 'ok'; myFeedback: MyFeedback} | {status: 'gone'};

// Append-only submit: create and edit are both a POST that supersedes the prior
// active response; a 404 means the signal is gone (discharged / off caseload).
export async function submitFeedback(input: {
  indicatorId: string;
  formId: string;
  answers: SubmittedAnswerRequest[];
}): Promise<SubmitFeedbackResult> {
  try {
    const response = await indicatorsFeedbackCreate(input.indicatorId, {
      formId: input.formId,
      answers: input.answers,
    });
    return {status: 'ok', myFeedback: response.data.myFeedback};
  } catch (error) {
    if (errorStatus(error) === 404) return {status: 'gone'};
    throw error;
  }
}

export const signalKeys = {
  all: ['signals'] as const,
  // Prefix shared by every filtered by-member cache — the feedback mutation
  // writes myFeedback across all of them regardless of the active filters.
  byMemberAll: ['signals', 'by-member'] as const,
  byMember: (params: ByMemberParams) =>
    [...signalKeys.byMemberAll, params] as const,
  programs: ['signals', 'programs'] as const,
  kinds: ['signals', 'kinds'] as const,
  feedbackForm: ['signals', 'feedback-form'] as const,
};

export function programsQueryOptions() {
  return queryOptions({
    queryKey: signalKeys.programs,
    queryFn: async (): Promise<ProgramSummary[]> => {
      const {data} = await programsList({pageSize: 100});
      return data.results.map((program) => ({
        id: program.id,
        name: program.name,
      }));
    },
    staleTime: Number.POSITIVE_INFINITY,
  });
}

// Read non-suspense so a not-yet-deployed endpoint degrades to just "All".
export function kindsQueryOptions() {
  return queryOptions({
    queryKey: signalKeys.kinds,
    queryFn: async (): Promise<SignalKind[]> => {
      const {data} = await indicatorsKindsList();
      return data;
    },
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function feedbackFormQueryOptions() {
  return queryOptions({
    queryKey: signalKeys.feedbackForm,
    queryFn: fetchFeedbackForm,
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function indicatorsByMemberInfiniteQueryOptions(
  filters: ByMemberFilters,
) {
  const params = filtersToParams(filters);
  return infiniteQueryOptions({
    queryKey: signalKeys.byMember(params),
    queryFn: ({pageParam}) => fetchIndicatorsByMember(params, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => extractCursor(lastPage.next),
    staleTime: 30_000,
    // Keep the current rows (and the filter bar) visible while a filter change refetches.
    placeholderData: keepPreviousData,
  });
}

// One member's full signal history. Keyed under `byMemberAll` (via the member
// param in the params key) so the shared feedback optimistic updates in
// useSignalFeedback/applyMyFeedback reach this cache with no extra wiring.
export function memberSignalsInfiniteQueryOptions(memberId: string) {
  return indicatorsByMemberInfiniteQueryOptions({
    program: 'all',
    kinds: [],
    member: memberId,
  });
}

export const NEVER_ACTIVATED_SCOPE =
  IndicatorsByMemberListScope['engagementnever_activated'];
export const NO_FIRST_ACTION_SCOPE =
  IndicatorsByMemberListScope['engagementno_first_action'];

type ScopedTabFilters = {program: string};

// Scope alone selects the tab's kind; sending `kind` too would couple params
// the API requires to agree.
function scopedTabQueryOptions(
  scope: typeof NEVER_ACTIVATED_SCOPE | typeof NO_FIRST_ACTION_SCOPE,
  filters: ScopedTabFilters,
) {
  return indicatorsByMemberInfiniteQueryOptions({
    program: filters.program,
    kinds: [],
    scope,
  });
}

export function neverLoggedInInfiniteQueryOptions(filters: ScopedTabFilters) {
  return scopedTabQueryOptions(NEVER_ACTIVATED_SCOPE, filters);
}

export function neverStartedInfiniteQueryOptions(filters: ScopedTabFilters) {
  return scopedTabQueryOptions(NO_FIRST_ACTION_SCOPE, filters);
}

// Set one indicator's `myFeedback` across every page of a by-member cache. Pure so
// the mutation reuses it for both the optimistic write and the echoed replacement.
export function applyMyFeedback(
  data: InfiniteData<IndicatorsByMemberPage> | undefined,
  indicatorId: string,
  myFeedback: MyFeedback,
): InfiniteData<IndicatorsByMemberPage> | undefined {
  if (!data) return data;
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      results: page.results.map((group) => ({
        ...group,
        indicators: group.indicators.map((indicator) =>
          indicator.id === indicatorId ? {...indicator, myFeedback} : indicator,
        ),
      })),
    })),
  };
}
